import { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import {loadRentObjects} from "@awe-web/shared/lib";
import {asyncAction} from "@awe-web/shared/lib";
import {getFixedCoStationId} from "@awe-web/shared/lib";
import {
  getContractTypeForB2bLTR,
  getContractTypeForB2bSTR,
  getContractTypeForB2cLTR,
  getContractTypeForB2cSTR
} from "../../config/store.js";
import {getRentObjectStationId} from "../../rent_contracts/util.js";
import {
  B2C_SelectStationAndTime,
  B2B_SelectStationAndTime,
  B2C_STR_SelectExtras,
  B2C_LTR_SelectExtras,
  B2B_STR_SelectExtras,
  B2B_LTR_SelectExtras
} from "../../routing/routes.js";
import {
  getCoDateTime,
  getCiDateTime,
  getRawCoStation,
  getRawCiStation,
  getCoDateTimeFromState,
  getCiDateTimeFromState
} from "../../stations/store.js";
import { setCiStation, setCoStation } from "../../stations/actions.js";
import { useInitialize } from "../../util/react.js";

const selectForReservationType = (reservationPath, isLtr, map) =>
  map[`${reservationPath} ${isLtr ? "LTR" : "STR"}`];

const getContractTypeForReservationType = (reservationPath, isLtr) =>
  selectForReservationType(reservationPath, isLtr, {
    "B2C STR": getContractTypeForB2cSTR,
    "B2C LTR": getContractTypeForB2cLTR,
    "B2B STR": getContractTypeForB2bSTR,
    "B2B LTR": getContractTypeForB2bLTR
  });

export const newInitializeSelectRentObjectScreen = ({reservationPath, isLtr}) => () => async (dispatch, getState, context) => {
  const coStationId = getRawCoStation(getState()).get('id');
  const ciStationId = getRawCiStation(getState()).get('id');
  const coDateTime = getCoDateTime(getState());
  const ciDateTime = getCiDateTime(getState());

  if (!coStationId || !ciStationId || !coDateTime || !ciDateTime) {
    const SelectStationAndTime = reservationPath === "B2C"
      ? B2C_SelectStationAndTime
      : B2B_SelectStationAndTime;
    return SelectStationAndTime.path();
  } else {
    const getContractType = getContractTypeForReservationType(reservationPath, isLtr);
    const contractType = getContractType(getState());
    const {
      coStationId: rentObjectStationId,
      pickupStationId: rentObjectPickupStationId
    } = getRentObjectStationId(coDateTime, ciDateTime, coStationId, reservationPath, getState());

    await Promise.all([
      dispatch(setCoStation(coStationId)),
      dispatch(setCiStation(ciStationId)),
      dispatch(loadRentObjects(contractType, rentObjectStationId, rentObjectPickupStationId))
    ]);
  }
};

export const newSelectRentObject = ({reservationPath, isLtr}) => (classificationId) => async (dispatch, getState, context) => {
  const getContractType = getContractTypeForReservationType(reservationPath, isLtr);
  const contractType = getContractType(getState());
  const userSelectedCoStationId = getRawCoStation(getState()).get('id');
  const ciStationId = getRawCiStation(getState()).get('id');
  const coDateTime = getCoDateTime(getState());
  const ciDateTime = getCiDateTime(getState());
  const fixedCoStationId = getFixedCoStationId(getState());

  const pickupStationId = fixedCoStationId ? userSelectedCoStationId : undefined;
  const coStationId = fixedCoStationId ? fixedCoStationId : userSelectedCoStationId;

  const rentContract = await dispatch(asyncAction({
    typePrefix: "SELECT_RENT_OBJECT/CREATE_RENT_CONTRACT",
    loadPayload: () => context.api.rentContracts.createRentContract({
      contractType,
      coStationId,
      coDateTime,
      ciStationId,
      ciDateTime,
      pickupStationId,
      classificationId
    })
  }));

  dispatch({
    type: "RENT_CONTRACTS/RENT_CONTRACT_LOADED",
    payload: {
      rentContract
    }
  });

  const SelectExtras = selectForReservationType(reservationPath, isLtr, {
    "B2C STR": B2C_STR_SelectExtras,
    "B2C LTR": B2C_LTR_SelectExtras,
    "B2B STR": B2B_STR_SelectExtras,
    "B2B LTR": B2B_LTR_SelectExtras
  });

  return SelectExtras.path();
};

export function useInitializeFromState(initCallback) {
  const coStation = useSelector(getRawCoStation);
  const ciStation = useSelector(getRawCiStation);
  const coStationId = useMemo(() => coStation ? coStation.get('id') : null, [coStation]);
  const ciStationId = useMemo(() => ciStation ? ciStation.get('id') : null, [ciStation]);
  const coDateTime = useSelector(getCoDateTimeFromState);
  const ciDateTime = useSelector(getCiDateTimeFromState);

  const init = useCallback(async () => {
    await initCallback();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initCallback, coStationId, ciStationId, coDateTime, ciDateTime]);

  return useInitialize(init);
}